import React, {
  FC,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { Button, Checkbox } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import Modal from 'components/shared/Modal';
import { useToast } from 'app/hooks/useToast';
import Logger from 'utils/logger';
import StripePaymentMethodFields from 'app/modules/settings/modules/billing/components/StripePaymentMethodFields';
import { useStripeSetupIntent } from 'app/modules/settings/modules/billing/hooks/useStripeSetupIntent';
import { useStripePaymentSubmit } from 'app/modules/settings/modules/billing/hooks/useStripePaymentSubmit';
import { useAttachStripePaymentMethod } from 'app/modules/settings/modules/billing/hooks/useAttachStripePaymentMethod';
import { getStripeJs } from 'app/modules/settings/modules/billing/hooks/getStripeJs';
import { ApiClientError } from 'api/client';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultEmail?: string;
  defaultHolderName?: string;
  showMakeDefault?: boolean;
};

type InnerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultEmail: string;
  defaultHolderName: string;
  clientSecret: string;
  showMakeDefault: boolean;
};

const AddStripePaymentMethodModalInner: FC<InnerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultEmail,
  defaultHolderName,
  clientSecret,
  showMakeDefault,
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [holderName, setHolderName] = useState(defaultHolderName);
  const [billingEmail, setBillingEmail] = useState(defaultEmail);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [makeDefault, setMakeDefault] = useState(true);

  const { submit } = useStripePaymentSubmit();
  const attachPaymentMethod = useAttachStripePaymentMethod();

  useEffect(() => {
    if (!isOpen) return;
    setErrorMessage(null);
    setHolderName(defaultHolderName);
    setBillingEmail(defaultEmail);
    setMakeDefault(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePaymentInputChange = () => {
    setErrorMessage(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!holderName || !billingEmail) {
      setErrorMessage(t('billing.stripe.fieldsRequired'));
      return;
    }

    setErrorMessage(null);
    setIsConfirming(true);
    const confirmResult = await submit({
      clientSecret,
      billingEmail,
      holderName,
      returnContext: { ctx: 'add', billingEmail, holderName },
    });
    setIsConfirming(false);

    if (!confirmResult.ok) {
      switch (confirmResult.error.kind) {
        case 'stripe':
          setErrorMessage(
            confirmResult.error.message || t('somethingWentWrong'),
          );
          break;
        default:
          setErrorMessage(t('somethingWentWrong'));
      }
      return;
    }

    try {
      await attachPaymentMethod.mutateAsync({
        body: {
          paymentMethodId: confirmResult.paymentMethodId,
          setupIntentId: confirmResult.setupIntentId,
          billingEmail,
          holderName,
          ...(showMakeDefault ? { makeDefault } : {}),
        },
        idempotencyKey: uuidv4(),
      });
      toast(t('billing.toasts.paymentMethodSaved'), 'success');
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (e) {
      const err = e as ApiClientError | undefined;
      const status = err?.axiosError?.response?.status;
      const userMessage = err?.userMessage;
      if (status === 422) {
        setErrorMessage(userMessage || t('billing.errors.stripeRefused'));
      } else if (status === 502) {
        setErrorMessage(t('billing.errors.processorUnavailable'));
      } else {
        setErrorMessage(userMessage || t('somethingWentWrong'));
      }
    }
  };

  const isSubmitting = attachPaymentMethod.isLoading || isConfirming;

  return (
    <>
      <Modal.Header title={t('billing.stripe.addCard.title')} />
      <Modal.Content className="tw-pb-6 tw-pt-4">
        <form
          id="add-stripe-payment-method-form"
          onSubmit={handleSubmit}
          className="tw-flex tw-flex-col tw-gap-4"
        >
          <StripePaymentMethodFields
            clientSecret={clientSecret}
            holderName={holderName}
            billingEmail={billingEmail}
            onHolderNameChange={setHolderName}
            onBillingEmailChange={setBillingEmail}
            onPaymentInputChange={handlePaymentInputChange}
            errorMessage={errorMessage}
          />
          {showMakeDefault && (
            <div className="tw-flex tw-items-start tw-gap-[10px]">
              <Checkbox
                id="add-stripe-payment-method_make-default"
                size="small"
                checked={makeDefault}
                onChange={() => null}
                onClick={() => setMakeDefault((value) => !value)}
                data-cy="add-stripe-payment-method_make-default"
              />
              <label
                htmlFor="add-stripe-payment-method_make-default"
                className="tw-flex tw-cursor-pointer tw-flex-col"
              >
                <span className="tw-text-sm tw-font-semibold tw-text-grayscale-180">
                  {t('billing.paymentMethods.useAsDefault')}
                </span>
                <span className="tw-text-xs tw-text-grayscale-100">
                  {t('billing.paymentMethods.useAsDefaultSub')}
                </span>
              </label>
            </div>
          )}
        </form>
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          data-cy="add-stripe-payment-method_cancel"
        >
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="add-stripe-payment-method-form"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          data-cy="add-stripe-payment-method_submit"
        >
          {t('billing.stripe.addCard.submit')}
        </Button>
      </Modal.Footer>
    </>
  );
};

type OuterInnerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultEmail: string;
  defaultHolderName: string;
  stripePromise: Promise<Stripe | null>;
  showMakeDefault: boolean;
};

const AddStripePaymentMethodModalOuter: FC<OuterInnerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultEmail,
  defaultHolderName,
  stripePromise,
  showMakeDefault,
}) => {
  const { t } = useTranslation();

  const setupIntentKey = useRef<string>(uuidv4());
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);

  const setupIntent = useStripeSetupIntent();

  useEffect(() => {
    if (!isOpen) return;
    setupIntentKey.current = uuidv4();
    setClientSecret(null);
    setErrorMessage(null);

    setupIntent
      .mutateAsync({ idempotencyKey: setupIntentKey.current })
      .then((res) => setClientSecret(res.clientSecret))
      .catch((e) => {
        Logger.error('createStripeSetupIntent failed', e);
        setErrorMessage(t('billing.errors.processorUnavailable'));
      });
    // We intentionally run this only when `isOpen` flips to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    let cancelled = false;
    stripePromise.then((stripe) => {
      if (cancelled) return;
      if (!stripe) {
        setStripeLoadFailed(true);
        setErrorMessage(t('billing.errors.processorUnavailable'));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [stripePromise, t]);

  if (!clientSecret || stripeLoadFailed) {
    return (
      <>
        <Modal.Header title={t('billing.stripe.addCard.title')} />
        <Modal.Content className="tw-pb-6 tw-pt-4">
          <StripePaymentMethodFields
            clientSecret={null}
            holderName={defaultHolderName}
            billingEmail={defaultEmail}
            onHolderNameChange={() => undefined}
            onBillingEmailChange={() => undefined}
            onPaymentInputChange={() => undefined}
            errorMessage={errorMessage}
          />
        </Modal.Content>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            data-cy="add-stripe-payment-method_cancel"
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="add-stripe-payment-method-form"
            isLoading={setupIntent.isLoading}
            disabled
            data-cy="add-stripe-payment-method_submit"
          >
            {t('billing.stripe.addCard.submit')}
          </Button>
        </Modal.Footer>
      </>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret }}
      key={clientSecret}
    >
      <AddStripePaymentMethodModalInner
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
        defaultEmail={defaultEmail}
        defaultHolderName={defaultHolderName}
        clientSecret={clientSecret}
        showMakeDefault={showMakeDefault}
      />
    </Elements>
  );
};

const AddStripePaymentMethodModal: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultEmail = '',
  defaultHolderName = '',
  showMakeDefault = false,
}) => {
  const stripePromise = useMemo(() => getStripeJs(), []);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      classNames={{
        modal: '!tw-max-h-[90vh] !tw-overflow-auto',
      }}
      isContentHeightAutoResizable={false}
      dataCy={{
        wrapper: 'add-stripe-payment-method_modal',
        closeButton: 'add-stripe-payment-method_close',
      }}
    >
      <AddStripePaymentMethodModalOuter
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
        defaultEmail={defaultEmail}
        defaultHolderName={defaultHolderName}
        stripePromise={stripePromise}
        showMakeDefault={showMakeDefault}
      />
    </Modal>
  );
};

export default AddStripePaymentMethodModal;
